import React, { Component, useEffect } from 'react';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import '@iwoca/orion/css/colors.css';
import '@iwoca/orion/css/radii.css';
import '@iwoca/orion/css/shadows.css';
import '@iwoca/orion/css/text.css';
import '@iwoca/orion/css/spacers.css';
import '@iwoca/orion/lib/css/orion.css';
import 'react-loading-skeleton/dist/skeleton.css';
import styles from './App.module.css';
import {
  CHECKOUT_BASE_PATH,
  CheckoutRoutes,
  SECURITY_CHECKS_BASE_PATH,
  SecurityCheckRoutes,
} from './Buyer/Checkout/routes';
import { OrderCheckoutPage } from './Buyer/Orders/OrderCheckoutPage/OrderCheckoutPage';
import OrderLandingPage from './Buyer/Orders/OrderLandingPage/OrderLandingPage';
import { GenericPayLinkLanding as GenericPayLinkLandingPage } from './Buyer/PayLinkLanding/GenericPayLinkLanding/GenericPayLinkLanding';
import { PayLinkLanding as PayLinkLandingPage } from './Buyer/PayLinkLanding/PayLinkLanding';
import { PayNowConfirmation } from './Buyer/PayNowConfirmation/PayNowConfirmation';
import { SpendingLimitApproved } from './Buyer/Signup/components/SpendingLimitDecision/SpendingLimitApproved';
import { SIGNUP_BASE_PATH, SignupRoutes } from './Buyer/Signup/routes';
import { SpendingLimitLanding } from './Buyer/SpendingLimit/SpendingLimitLanding';
import { SellerNavBar } from './components/NavBars/SellerNavBar/SellerNavBar';
import { PageTemplate } from './components/PageTemplate/PageTemplate';
import pageTemplateStyles from './components/PageTemplate/PageTemplate.module.css';
import { useOnMount } from './hooks/useOnMount';
import { GenericError } from './Pages/FailurePages/GenericError';
import { PayLinkNotFound } from './Pages/FailurePages/PayLinkNotFound';
import { XeroPayLinkError } from './Pages/FailurePages/XeroPayLinkError/XeroPayLinkError';
import { PageNotFound } from './Pages/PageNotFound/PageNotFound';
import { AppRoutes } from './routing/app.routes';
import { AccountActivation as AccountActivationPage } from './Seller/AccountActivation/AccountActivation';
import { NotOnboarded } from './Seller/Shopify/NotOnboarded/NotAOnboarded';
import { SellerXeroContextProvider } from './Seller/Xero/Xero.context';
import { IwQueryClientProvider } from './store/IwQueryClientProvider';
import logger from './utils/logger';
import { initialise } from './utils/tracking';

type TFSUserVars = Record<string, number | boolean | string>;
declare global {
  interface Window {
    FS?: {
      setUserVars: (values: TFSUserVars) => void;
      identify: (id: string, values?: TFSUserVars) => void;
    };
  }
}

// react-router doesn't support this but reach-router did, copied from here:
// https://github.com/remix-run/react-router/blob/main/docs/upgrading/reach.md#what-about-clicking-links-that-arent-updated
const Redirect = ({ to }: { to: string }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = window.location.search ? `?${params}` : '';
    navigate(`${to}${query}`);
  });

  return null;
};

export const App = () => {
  useOnMount(() => {
    initialise();
  });

  return (
    <IwQueryClientProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <ErrorBoundary>
        <Routes>
          <Route path="/pay/404/" element={<PageNotFound />} />
          <Route path={`${SIGNUP_BASE_PATH}*`} element={<SignupRoutes />} />
          <Route
            path={`${CHECKOUT_BASE_PATH}/*`}
            element={<CheckoutRoutes />}
          />
          <Route
            path={`${SECURITY_CHECKS_BASE_PATH}/*`}
            element={<SecurityCheckRoutes />}
          />
          <Route path="/*" element={<SellerDashboardRoutes />} />
        </Routes>
      </ErrorBoundary>
    </IwQueryClientProvider>
  );
};

const SellerDashboardRoutes = () => {
  return (
    <div className={styles.App}>
      <ErrorBoundary>
        <div className={styles.router}>
          <Routes>
            <Route
              path="pay/seller/not-onboarded/"
              element={<NotOnboarded />}
            />
            <Route
              path="pay/spending-account/*"
              element={<SpendingLimitRoutes />}
            />
            <Route
              path="pay/contract/update/"
              element={<Redirect to="/pay/pay-links/universal" />}
            />
            <Route
              path="/pay/to/:seller/:payLinkId/"
              element={<PayLinkLanding />}
            />
            <Route
              path="pay/me/:sellerHandle/package/:payLinkPackageId/"
              element={<GenericPayLink />}
            />
            <Route path="pay/me/:sellerHandle" element={<GenericPayLink />} />
            <Route
              path="pay/donation/mental-health-uk/"
              element={<PayLinkLandingDemo />}
            />
            <Route
              path="pay/pay-now/confirmation/"
              element={<PayNowConfirmation />}
            />
            <Route
              path="pay/onboarding/*"
              element={<Redirect to="/pay/activation/" />}
            />
            <Route path="pay/order/*" element={<OrderRoutes />} />
            <Route path="pay/xero-error/" element={<XeroPayLinkError />} />
            <Route path="pay/activation/" element={<AccountActivation />} />
            <Route
              path="pay/pay-links/"
              element={<Redirect to="/pay/pay-links/universal" />}
            />
          </Routes>
          <AppRoutes />
        </div>
      </ErrorBoundary>
    </div>
  );
};

const SpendingLimitRoutes = () => (
  <Routes>
    <Route path=":sellerHandle/" element={<SpendingLimitLanding />} />
    <Route path="approved/" element={<SpendingLimitApproved />} />
  </Routes>
);

const OrderRoutes = () => (
  <Routes>
    <Route path=":orderId/landing/" element={<OrderLandingPage />} />
    <Route path=":orderId/" element={<OrderCheckoutPage />} />
  </Routes>
);

const AccountActivation = () => {
  return (
    <SellerXeroContextProvider>
      <PageTemplate NavBar={() => <SellerNavBar />}>
        <AccountActivationPage />
      </PageTemplate>
    </SellerXeroContextProvider>
  );
};

const GenericPayLink = () => {
  const { sellerHandle, payLinkPackageId } = useParams();

  return (
    <main className={pageTemplateStyles.AppMain}>
      {sellerHandle && (
        <GenericPayLinkLandingPage
          sellerHandle={sellerHandle}
          payLinkPackageId={payLinkPackageId}
        />
      )}
    </main>
  );
};

const PayLinkLanding = () => {
  const { payLinkId } = useParams();

  return (
    <main className={pageTemplateStyles.AppMain}>
      {payLinkId ? (
        <PayLinkLandingPage payLinkId={payLinkId} />
      ) : (
        <PayLinkNotFound />
      )}
    </main>
  );
};

const PayLinkLandingDemo = () => {
  const MENTAL_HEALTH_UK_2_POUND_PAY_LINK_ID =
    '6977a0b1-59a9-4ef4-a532-1b3d6fc928dc';
  return (
    <main className={pageTemplateStyles.AppMain}>
      <PayLinkLandingPage payLinkId={MENTAL_HEALTH_UK_2_POUND_PAY_LINK_ID} />
    </main>
  );
};

export class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ hasError: true });
    logger.error(error.toString(), { stack: error.stack, ...info });
  }

  render() {
    if (this.state.hasError) {
      return <GenericError errorText="something went wrong." />;
    }
    return this.props.children;
  }
}
